import {Box, Button, Paper, Typography} from "@mui/material";
import {styled} from "@mui/material/styles";
import {OverridableStringUnion} from "@mui/types";
import {ButtonPropsColorOverrides} from "@mui/material/Button/Button";
import {pxToRem} from "@/theme/typography";
import {styledOmit$PropsOptions} from "@utils/commonUtils";

type FlexBoxProps = {
    $column?: boolean
    $row?: boolean
    $fullWidth?: boolean
}

export const FlexBox = styled(Box, styledOmit$PropsOptions)<FlexBoxProps>(
    ({$column, $row, $fullWidth}) => ({
        display: 'flex',
        ...($row && {
            flexDirection: 'row'
        }),
        ...($column && {
            flexDirection: 'column'
        }),
        ...($fullWidth && {
            width: '100%'
        })
    })
)

export const VisuallyHiddenInput = styled('input')({
    clip: 'rect(0 0 0 0)',
    clipPath: 'inset(50%)',
    height: 1,
    overflow: 'hidden',
    position: 'absolute',
    bottom: 0,
    left: 0,
    whiteSpace: 'nowrap',
    width: 1,
});

interface IconBoxProps {
    color?: OverridableStringUnion<
        'inherit' | 'primary' | 'secondary' | 'success' | 'error' | 'info' | 'warning',
        ButtonPropsColorOverrides
    >;
}

export const IconBox = styled(Box)<IconBoxProps>(
    ({theme, color = 'eliga_7FF'}) => ({
        width: '34px',
        height: '34px',
        backgroundColor: theme.palette[color].main,
        borderRadius: '8px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        '& svg': {
            width: '18px',
            height: '18px',
            fill: theme.palette[color].contrastText
        }
    })
)

export interface TextInfoBoxProps {
    maxWidth?: number;
    minWidth?: number;
    padding?: string;
}

export const TextInfoBox = styled(FlexBox)<TextInfoBoxProps>(
    ({theme, maxWidth, minWidth, padding}) => ({
        borderRadius: theme.shape.borderRadius,
        padding: padding ? padding : '10px 8px',
        backgroundColor: theme.palette.eliga_6FA.main,
        maxWidth: maxWidth ? `${maxWidth}px` : '100%',
        minWidth: minWidth ? `${minWidth}px` : 'auto',
        fontSize: pxToRem(14)
    })
)

interface StyleTextProps {
    color?: string;
    fontSize?: number;
    $bold?: boolean;
}

export const StyleText = styled(Typography)<StyleTextProps>(
    ({theme, color, fontSize, $bold}) => ({
        display: 'inline-block',
        color: color ? color : 'eliga_42C.main',
        fontSize: fontSize ? pxToRem(fontSize) : pxToRem(14),
        fontWeight: $bold ? 'bold' : 'normal'
    })
)

type ToggleItemChipProps = {
    $selected?: boolean
}
export const ToggleItemChip = styled(Paper, styledOmit$PropsOptions)<ToggleItemChipProps>(
    ({theme, $selected}) => ({
        backgroundColor: $selected ? theme.palette.eliga_E3FC.main : theme.palette.eliga_0F5.main,
        ...theme.typography.body2,
        padding: theme.spacing(1),
        borderRadius: '6px',
        textAlign: 'center',
        cursor: 'pointer',
        color: $selected ? theme.palette.eliga_168.main : theme.palette.eliga_399.main,
    }));

export const DetailWrap = styled(FlexBox)(
    ({theme}) => ({
        padding: theme.spacing(2, 0),
        flexDirection: 'column',
    })
)

type DetailTitleContentProps = {
    $borderRadius?: boolean
}
export const DetailTitle = styled(Paper, styledOmit$PropsOptions)<DetailTitleContentProps>(
    ({theme, $borderRadius}) => ({
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: theme.palette.mode === 'dark' ? '#1A2027' : theme.palette.eliga_6FA.main,
        ...theme.typography.body2,
        padding: theme.spacing(1),
        textAlign: 'center',
        height: '100%',
        color: theme.palette.eliga_399.main,
        ...($borderRadius && {
            borderRadius: '8px 0 0',
        }),
    })
);

export const DetailContents = styled(Paper, styledOmit$PropsOptions)<DetailTitleContentProps>(
    ({theme, $borderRadius}) => ({
        ...theme.typography.body2,
        // padding: theme.spacing(2,0),
        padding: theme.spacing(2),
        [theme.breakpoints.down('md')]: {
            padding: theme.spacing(2, 0)
        },
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        justifyContent: 'center',
        color: theme.palette.primary.main,
        wordBreak: 'break-word',
        ...($borderRadius && {
            borderRadius: '0 8px 0 0',
        }),

    })
);

export const BorderButton = styled(Button)(
    ({theme}) => ({
        backgroundColor: theme.palette.common.white,
        border: '1px solid #E3E6ED',
        whiteSpace: 'nowrap',
    })
)

export const NoImageItemForList = styled(FlexBox)(
    ({theme}) => ({
        display: "inline-flex",
        backgroundColor: theme.palette.eliga_6ED.main,
        justifyContent: 'center',
        alignItems: 'center',
        width: '42px',
        height: '42px',
    })
)

export const NoImageWrapBox  = styled(FlexBox)(
    ({theme}) => ({
        display: "inline-flex",
        backgroundColor: theme.palette.eliga_6ED.main,
        justifyContent: 'center',
        alignItems: 'center',
        width: '100%',
        height: '100%',
    })
)

export const PickupGapFlexBox = styled(FlexBox)({
    gap: "10px",
    width: "100%",
});

export const PickupItemContainer = styled(PickupGapFlexBox)(({theme}) => ({
    backgroundColor: theme.palette.grey[0],
    borderRadius: "8px",
    padding: "10px",
}));


export const SelectFileContainer = styled(FlexBox)(
    ({theme}) => ({
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '8px',
        backgroundColor: theme.palette.eliga_6FA.main,
        borderRadius: '6px',
        '& .MuiGrid-item': {
            borderBottom: '1px solid #EEF0F5'
        }
    })
);

export const FileIconBox = styled(Box)(
    ({theme}) => ({
        width: '20px',
        height: '20px',
        backgroundColor: theme.palette.eliga_6ED.main,
        borderRadius: '8px',
        marginRight: '8px',
        padding: '2px',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        '& svg': {
            width: '100%',
            fill: theme.palette.common.white,
        }
    })
);

export const UpdateBox = styled(FlexBox)(({theme}) => ({
    width: '20%',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '20px 10px',
    backgroundColor: theme.palette.eliga_FFF.main,
    borderRadius: '18px'
}));

export const UpdateText = styled(Typography)(({theme}) => ({
    fontSize: '10px',
    color: theme.palette.eliga_6B6.main,
    fontWeight: 500
}));

export const CaptionText = styled(Typography)(({theme}) => ({
    backgroundColor: theme.palette.eliga_6FA.main,
    padding: '6px',
    width: 'fit-content'
}));

export const BackgroundBox = styled(Box)(
    ({theme}) => ({
        backgroundColor: theme.palette.eliga_6FA.main,
        borderRadius: '4px',
        padding: '6px',
        fontSize: '14px',
        color: theme.palette.eliga_168.main
    })
);

export const InfoBox = styled(Box)<{$isMobile?: boolean}>(
    ({theme, $isMobile}) => ({
        width: 'auto',
        padding: $isMobile ? '15px 45px':'15px 100px',
        backgroundColor: theme.palette.eliga_6FA.main,
        borderRadius: '10px',
        display: 'flex',
        justifyContent: 'center'
    })
);